import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import PrivateReportImageBridge from './components/common/PrivateReportImageBridge';
import WhatsAppSupport from './components/common/WhatsAppSupport';
import AnalyticsBridge from './components/common/AnalyticsBridge';
import ReportActivityAnalytics from './components/common/ReportActivityAnalytics';
import LandingPage from './pages/LandingPage';
import Recursos from './pages/Recursos';
import Planos from './pages/Planos';
import ComoFunciona from './pages/ComoFunciona';
import Dashboard from './pages/Dashboard';
import OnboardingStart from './pages/OnboardingStart';
import Templates from './pages/Templates';
import PublicTemplates from './pages/PublicTemplates';
import SeoReferencePage from './pages/SeoReferencePage';
import SeoCommercialPage from './pages/SeoCommercialPage';
import TrustPage from './pages/TrustPage';
import Signatures from './pages/Signatures';
import Locations from './pages/Locations';
import NewReport from './pages/NewReport';
import EditReport from './pages/EditReport';
import History from './pages/History';
import ReportView from './pages/ReportView';
import AuditCertificate from './pages/AuditCertificate';
import Profile from './pages/Profile';
import Login from './pages/Login';
import SubscriptionResult from './pages/SubscriptionResult';
import AdminModeration from './pages/AdminModeration';
import AdminBackoffice from './pages/AdminBackoffice';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { appUrl, isAppPath, isPublicPath, legacyAppPathToClean, publicUrl, resolveSiteMode } from './utils/routing';

function ExternalRedirect({ to, replace = true }) {
  useEffect(() => {
    if (replace) window.location.replace(to);
    else window.location.assign(to);
  }, [to, replace]);
  return null;
}

function EditReportRedirect() {
  const { id } = useParams();
  return <Navigate to={`/editar-laudo/${id}`} replace />;
}

function LegacyAppRedirect() {
  const location = useLocation();
  const target = `${legacyAppPathToClean(location.pathname)}${location.search || ''}${location.hash || ''}`;
  return <ExternalRedirect to={appUrl(target)} />;
}

function CrossDomainGuard({ mode, children }) {
  const location = useLocation();
  const path = location.pathname;
  const suffix = `${location.search || ''}${location.hash || ''}`;

  if (mode === 'public' && isAppPath(path)) {
    const targetPath = path.startsWith('/app') ? legacyAppPathToClean(path) : path;
    return <ExternalRedirect to={appUrl(`${targetPath}${suffix}`)} />;
  }

  if (mode === 'app' && isPublicPath(path) && path !== '/planos') {
    return <ExternalRedirect to={`${publicUrl(path)}${suffix}`} />;
  }

  return children;
}

function LoggedShell() {
  return (
    <ProtectedRoute>
      <div className="flex flex-col min-h-screen">
        <PrivateReportImageBridge />
        <ReportActivityAnalytics />
        <Navbar />
        <WhatsAppSupport />
        <main className="flex-1 w-full bg-paper-alt">
          <Routes>
            <Route path="painel" element={<Dashboard />} />
            <Route path="comece" element={<OnboardingStart />} />
            <Route path="perfil" element={<Profile />} />
            <Route path="modelos" element={<Templates />} />
            <Route path="assinaturas" element={<Signatures />} />
            <Route path="locais" element={<Locations />} />
            <Route path="novo-laudo" element={<NewReport />} />
            <Route path="editar-laudo/:id" element={<EditReport />} />
            <Route path="meus-laudos" element={<History />} />
            <Route path="planos" element={<Planos />} />
            <Route path="moderacao" element={<AdminModeration />} />
            <Route path="admin" element={<AdminBackoffice />} />
            <Route path="dashboard" element={<Navigate to="/painel" replace />} />
            <Route path="profile" element={<Navigate to="/perfil" replace />} />
            <Route path="templates" element={<Navigate to="/modelos" replace />} />
            <Route path="signatures" element={<Navigate to="/assinaturas" replace />} />
            <Route path="locations" element={<Navigate to="/locais" replace />} />
            <Route path="new-report" element={<Navigate to="/novo-laudo" replace />} />
            <Route path="edit-report/:id" element={<EditReportRedirect />} />
            <Route path="history" element={<Navigate to="/meus-laudos" replace />} />
            <Route path="moderation" element={<Navigate to="/moderacao" replace />} />
            <Route index element={<Navigate to="/painel" replace />} />
            <Route path="*" element={<Navigate to="/painel" replace />} />
          </Routes>
        </main>
      </div>
    </ProtectedRoute>
  );
}

function PublicRoutes() {
  return (
    <CrossDomainGuard mode="public">
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/recursos" element={<Recursos />} />
        <Route path="/planos" element={<Planos />} />
        <Route path="/como-funciona" element={<ComoFunciona />} />
        <Route path="/sobre" element={<TrustPage />} />
        <Route path="/seguranca" element={<TrustPage />} />
        <Route path="/privacidade" element={<TrustPage />} />
        <Route path="/modelos-laudo/*" element={<PublicTemplates />} />
        <Route path="/referencias-laudos/*" element={<SeoReferencePage />} />
        <Route path="/sistema-de-laudos-medicos" element={<SeoCommercialPage />} />
        <Route path="/software-para-laudos-medicos" element={<SeoCommercialPage />} />
        <Route path="/programa-para-laudos-medicos" element={<SeoCommercialPage />} />
        <Route path="/laudo/:id" element={<ReportView />} />
        <Route path="/laudo/:id/certificado" element={<AuditCertificate />} />
        <Route path="/report/:id" element={<ReportView />} />
        <Route path="/report/:id/certificate" element={<AuditCertificate />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </CrossDomainGuard>
  );
}

function AppRoutes() {
  return (
    <CrossDomainGuard mode="app">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/assinatura/sucesso" element={<ProtectedRoute><SubscriptionResult success={true} /></ProtectedRoute>} />
        <Route path="/assinatura/cancelado" element={<ProtectedRoute><SubscriptionResult success={false} /></ProtectedRoute>} />
        <Route path="/app/*" element={<LegacyAppRedirect />} />
        <Route path="/*" element={<LoggedShell />} />
      </Routes>
    </CrossDomainGuard>
  );
}

function RoutedApp() {
  const mode = resolveSiteMode(window.location.hostname, window.location.pathname);
  return mode === 'app' ? <AppRoutes /> : <PublicRoutes />;
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <AnalyticsBridge />
          <div className="min-h-screen bg-white flex flex-col">
            <RoutedApp />
          </div>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
